import React from 'react';
import { Image } from '../Image/Image';
import './SiteWideBanner.scss';

/**
 * SiteWideBanner - Banner subcomponent for MegaMenu
 * 
 * A lightweight banner component for displaying important announcements above the navigation.
 * Part of the MegaMenu component hierarchy, not exported as a standalone component.
 * 
 * Extends Cloudscape Link with CMS convenience features:
 * - `iconUrl` - Optional icon image URL
 * - `iconAlt` - Icon alt text for accessibility
 * - Flat props structure for Polyphemus CMS compatibility
 * - 7 configurable background color presets using Cloudscape design tokens
 * - Analytics support via data attributes (CTA ID, LD appendix)
 * 
 * @example
 * ```tsx
 * <SiteWideBanner
 *   iconUrl="https://cdn.example.com/icons/star.svg"
 *   iconAlt="Star"
 *   text="New feature available!"
 *   linkText="Learn how"
 *   linkUrl="/features"
 *   backgroundColor="blue"
 *   ctaId="banner-cta"
 * />
 * ```
 */

export interface SiteWideBannerProps {
  /** Optional icon image URL */
  iconUrl?: string;
  
  /** Banner text content */
  text?: string;
  
  /** Optional link text */
  linkText?: string;
  
  /** Optional link URL */
  linkUrl?: string;
  
  /** CTA ID for analytics tracking */
  ctaId?: string;
  
  /** LD appendix for analytics tracking */
  ldAppendix?: string;
  
  /** 
   * Background color preset
   * Currently only 'green' is supported; additional presets coming in future tasks
   * @default 'green'
   */
  backgroundColor?: 'green';
  
  /** Optional right-aligned content (e.g. SearchBar, LanguagePicker) */
  rightContent?: React.ReactNode;
}

export const SiteWideBanner: React.FC<SiteWideBannerProps> = ({
  iconUrl,
  text,
  linkText,
  linkUrl,
  ctaId,
  ldAppendix,
  backgroundColor = 'green',
  rightContent,
}) => {
  return (
    <div 
      className={`poly-site-wide-banner poly-site-wide-banner--${backgroundColor}`}
      role="banner"
    >
      <div className="poly-site-wide-banner__container">
        <div className="poly-site-wide-banner__content">
          {iconUrl && (
            <span className="poly-site-wide-banner__icon">
              <Image
                url={iconUrl}
                alt=""
                width={20}
                height={20}
                aria-hidden="true"
              />
            </span>
          )}
          
          {text && (
            <span className="poly-site-wide-banner__text">
              {text}
            </span>
          )}
          
          {linkText && linkUrl && (
            <a
              href={linkUrl}
              className="poly-site-wide-banner__link"
              data-cta-id={ctaId}
              data-ld-append={ldAppendix}
            >
              {linkText}
            </a>
          )}
        </div>

        {rightContent && (
          <div className="poly-site-wide-banner__right">
            {rightContent}
          </div>
        )}
      </div>
    </div>
  );
};
